
import React, { useState } from 'react';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import FileUploader from './components/FileUploader';
import ResultsDisplay from './components/ResultsDisplay';
import { User, GeneratedContent, FileData, UploadMode } from './types';
import { processContent } from './services/geminiService';
import { AlertCircle } from 'lucide-react';

const App: React.FC = () => {
  const [user, setUser] = useState<User>({ email: '', isLoggedIn: false });
  const [view, setView] = useState<'home' | 'dashboard' | 'results'>('home');
  const [loginEmail, setLoginEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generatedResults, setGeneratedResults] = useState<GeneratedContent | null>(null);

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (loginEmail.includes('@')) {
      setUser({ email: loginEmail, isLoggedIn: true });
      setView('dashboard');
    }
  };

  const handleLogout = () => {
    setUser({ email: '', isLoggedIn: false });
    setView('home');
    setGeneratedResults(null);
  };

  const handleProcess = async (data: string | FileData, mode: UploadMode) => {
    setIsLoading(true);
    setError(null);
    try {
      const results = await processContent(data, mode);
      setGeneratedResults(results);
      setView('results');
    } catch (err: any) {
      setError(err.message || 'Failed to process content. Please try again.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar user={user} onLogout={handleLogout} />

      <main className="flex-grow">
        {view === 'home' && (
          <Hero onGetStarted={() => setView('dashboard')} />
        )}

        {view === 'dashboard' && !user.isLoggedIn && (
          <div className="max-w-md mx-auto mt-24 p-8 bg-white rounded-3xl shadow-xl border border-slate-100">
            <div className="text-center mb-8">
              <div className="w-20 h-20 rounded-2xl overflow-hidden border border-slate-100 shadow-md mx-auto mb-6">
                <img 
                  src="https://images.unsplash.com/photo-1614850523296-d8c1af93d400?w=200&h=200&fit=crop&q=80" 
                  alt="WordCast Logo"
                  className="h-full w-full object-cover"
                />
              </div>
              <h2 className="text-2xl font-bold text-slate-900">Sign in to Start</h2>
              <p className="text-slate-500 mt-2">No password needed for this MVP.</p>
            </div>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Email Address</label>
                <input
                  type="email"
                  required
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                  placeholder="name@example.com"
                />
              </div>
              <button
                type="submit"
                className="w-full py-4 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 transition-all shadow-lg hover:shadow-indigo-200"
              >
                Continue
              </button>
            </form>
          </div>
        )}

        {view === 'dashboard' && user.isLoggedIn && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-slate-900">Upload Your Message</h2>
              <p className="text-slate-600 mt-2">Paste text or upload media to begin transformation.</p>
            </div>
            
            {error && (
              <div className="max-w-4xl mx-auto mb-6 bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3 text-red-600">
                <AlertCircle size={20} />
                <p className="font-medium">{error}</p>
              </div>
            )}

            <FileUploader onProcess={handleProcess} isLoading={isLoading} />
          </div>
        )}

        {view === 'results' && generatedResults && (
          <ResultsDisplay 
            content={generatedResults} 
            onReset={() => setView('dashboard')} 
          />
        )}
      </main>

      <footer className="bg-white border-t border-slate-100 py-12">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-slate-400 text-sm">
            © {new Date().getFullYear()} WordCast. 
            <br className="sm:hidden" /> Created for churches, teachers, and speakers.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default App;
